import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Palette, PlusCircle, Eraser, Wand2, Coins } from 'lucide-react';
import { Button, Textarea } from '@/components/v2/ui';
import { retouchImageOptimize } from '@/lib/api/image-optimize';
import { getCreditPricing } from '@/lib/api/credits';
import { type ImageOptimizeState } from './useImageOptimizeState';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

type RetouchAction = 'recolor' | 'add' | 'remove' | 'custom';

const ACTION_ICONS: Record<RetouchAction, React.ReactNode> = {
  recolor: <Palette className="h-4 w-4" />,
  add: <PlusCircle className="h-4 w-4" />,
  remove: <Eraser className="h-4 w-4" />,
  custom: <Wand2 className="h-4 w-4" />,
};

export function RetouchGroup({ state, setState }: Props) {
  const { t, i18n } = useTranslation();
  const [action, setAction] = useState<RetouchAction | null>(null);
  const [input, setInput] = useState('');
  const [color, setColor] = useState('#000000');

  const { data: pricingData } = useQuery({
    queryKey: ['credit-pricing'],
    queryFn: getCreditPricing,
    staleTime: 60 * 60 * 1000,
  });
  const creditCost = pricingData?.pricing?.find(
    p => p.feature_code === 'image_generation'
  )?.effective_credits ?? 0;

  const handleSelect = useCallback((a: RetouchAction) => {
    setAction(prev => prev === a ? null : a);
    setInput('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!state.currentFile) return;
    setState(prev => ({ ...prev, isEditing: true }));
    try {
      const result = await retouchImageOptimize(state.currentFile, {
        action: action!,
        prompt: input || undefined,
        color: action === 'recolor' ? color : undefined,
        analysis_context: state.analysisResult ? JSON.stringify(state.analysisResult) : undefined,
        language: i18n.language,
        source_s3_key: state.editedS3Key || undefined,
      });
      setState(prev => ({
        ...prev,
        isEditing: false,
        editedImageUrl: result.image_url,
        editedS3Key: result.s3_key,
        editHistory: [...prev.editHistory, {
          timestamp: new Date(),
          action: action ?? 'custom',
          labelKey: `imageOptimize.actions.${action === 'add' ? 'addObject' : action === 'remove' ? 'removeElement' : action === 'custom' ? 'customPrompt' : action}`,
          imageUrl: result.image_url,
          s3Key: result.s3_key,
        }],
      }));
    } catch {
      setState(prev => ({ ...prev, isEditing: false }));
    }
  }, [state.currentFile, state.analysisResult, state.editedS3Key, setState, action, input, color, i18n.language]);

  const canSubmit = action && (action === 'recolor' || input.trim());

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {(['recolor', 'add', 'remove', 'custom'] as const).map(a => (
          <Button key={a} variant={action === a ? 'outline' : 'ghost'} size="sm"
            className={action === a ? 'border-orange-500 ring-1 ring-orange-500' : ''}
            iconLeft={ACTION_ICONS[a]} onClick={() => handleSelect(a)}>
            {t(`imageOptimize.actions.${a === 'add' ? 'addObject' : a === 'remove' ? 'removeElement' : a === 'custom' ? 'customPrompt' : a}`)}
          </Button>
        ))}
      </div>

      {action === 'recolor' && (
        <div className="flex items-center gap-2">
          <input type="color" value={color} onChange={e => setColor(e.target.value)}
            className="w-10 h-10 rounded border border-neutral-200 cursor-pointer" />
          <input type="text" value={color} onChange={e => setColor(e.target.value)}
            className="border border-neutral-300 rounded px-2 py-1 text-sm w-24 font-mono" />
        </div>
      )}

      {action && (
        <Textarea placeholder={t(action === 'recolor' ? 'imageOptimize.actions.recolorPlaceholder' : 'imageOptimize.actions.promptPlaceholder')}
          value={input} onChange={e => setInput(e.target.value)} rows={3} />
      )}

      {action && (<>
        {creditCost > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <Coins className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            <span className="text-xs text-amber-700 dark:text-amber-300">
              {t('imageOptimize.retouch.creditCost', { credits: creditCost })}
            </span>
          </div>
        )}
        <Button variant="warning" className="w-full" disabled={!canSubmit || state.isEditing}
          loading={state.isEditing} onClick={handleSubmit}>
          {t('imageOptimize.actions.apply')}
        </Button>
      </>)}
    </div>
  );
}
